<template>
  <Teleport defer to="ion-app">
    <ion-modal
      ref="ionModal"
      :can-dismiss="canDismiss"
      :backdrop-dismiss="backdropDismiss"
      :show-backdrop="true"
      @ionModalDidDismiss="onDismiss"
      @ionModalWillDismiss="onWillDismiss"
      class="app-modal"
    >
      <div class="modal-overlay" :class="safeAreaClass" @click="close">
        <div class="modal-container" :class="[containerClass]" @click.stop>
          <button v-if="showClose" class="modal-close-btn" @click="close" aria-label="Close modal">
            <CloseIcon />
          </button>

          <div class="modal-header">
            <h3 class="modal-title">
              <slot name="header"></slot>
            </h3>
          </div>

          <div class="modal-content">
            <slot></slot>
          </div>

          <div class="modal-footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </ion-modal>
  </Teleport>
</template>

<script>
import { IonModal } from '@ionic/vue';
import CloseIcon from '/public/assets/icons/board/CloseIcon.vue';
import { isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';

export default {
  name: 'Modal',
  components: {
    IonModal,
    CloseIcon,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    canDismiss: {
      type: Boolean,
      default: true,
    },
    backdropDismiss: {
      type: Boolean,
      default: true,
    },
    containerClass: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  computed: {
    safeAreaClass() {
      if (isIosNativeOnly()) {
        return 'ios-safe-area';
      }
      if (isAndroidNativeOnly()) {
        return 'android-safe-area';
      }
      return '';
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.$refs.ionModal?.$el?.present();
      } else {
        this.$refs.ionModal?.$el?.dismiss();
      }
    },
  },
  mounted() {
    if (this.modelValue) {
      this.$nextTick(() => this.$refs.ionModal?.$el?.present());
    }
  },
  methods: {
    close() {
      if (!this.canDismiss) return;
      this.$emit('update:modelValue', false);
    },
    onDismiss() {
      this.$emit('update:modelValue', false);
    },
    onWillDismiss() {},
  },
};
</script>

<style scoped>
.app-modal {
  --width: 100%;
  --height: 100%;
  --max-width: 100%;
  --max-height: 100%;
  --background: transparent;
  --backdrop-opacity: 0.5;
}

.app-modal::part(backdrop) {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.modal-container {
  position: relative;
  width: 100%;
  max-width: 560px;
  background: #f6f4f0;
  border-radius: 10px;
  padding: 24px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Responsive padding */
@media (min-width: 768px) {
  .modal-container {
    padding: 32px;
    max-width: 640px;
  }
}

@media (min-width: 1024px) {
  .modal-container {
    max-width: 960px;
  }
}

/* iOS safe area */
.ios-safe-area.modal-overlay {
  padding-top: max(16px, env(safe-area-inset-top));
  padding-bottom: max(16px, env(safe-area-inset-bottom));
}

/* Android safe area */
.android-safe-area.modal-overlay {
  padding-top: max(16px, env(safe-area-inset-top));
  padding-bottom: max(16px, env(safe-area-inset-bottom));
}

/* Close button */
.modal-close-btn {
  position: absolute;
  right: 24px;
  top: 25px;
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #9ca3af;
  transition: color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  color: #6b7280;
}

.modal-close-btn:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 2px;
  border-radius: 4px;
}

/* Header */
.modal-header {
  margin-bottom: 24px;
}

.modal-title {
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  font-weight: 900;
  font-size: 20px;
  line-height: 1.2;
  color: #2b2926;
  letter-spacing: -0.03em;
  margin: 0;
}

@media (min-width: 768px) {
  .modal-title {
    font-size: 24px;
  }
}

@media (min-width: 1024px) {
  .modal-title {
    font-size: 28px;
  }
}

/* Content */
.modal-content {
  margin-top: 8px;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 8px;
}

/* Custom scrollbar */
.modal-content::-webkit-scrollbar {
  width: 6px;
}

.modal-content::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.modal-content::-webkit-scrollbar-thumb {
  background: #a5a4a3;
  border-radius: 3px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background: #8c8a85;
}

/* Footer */
.modal-footer {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Allow custom class to override spacing */
.modal-container.modal-custom-spacing {
  padding: 16px;
}

.modal-container.modal-custom-spacing .modal-header {
  margin-bottom: 16px;
}

.modal-container.modal-custom-spacing .modal-footer {
  margin-top: 16px;
}

/* Responsive max-height for modal content */
@media (max-height: 667px) {
  .modal-content {
    max-height: 50vh;
  }
}

@media (max-height: 844px) {
  .modal-content {
    max-height: 55vh;
  }
}

@media (min-height: 845px) {
  .modal-content {
    max-height: 60vh;
  }
}
</style>
